import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import PropertyCard from "@/components/PropertyCard";
import SEO from "@/components/SEO";
import Breadcrumbs from "@/components/Breadcrumbs";
import FAQSection from "@/components/FAQSection";
import { LOCALITY_FAQS } from "@/data/faqs";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/lib/auth";
import { MapPin, BadgeCheck, TrendingUp, Users, Sparkles } from "lucide-react";

// Slug helpers
const slugify = (s) => s.toLowerCase().replace(/\s+/g, "-").replace(/[^a-z0-9-]/g, "");
const deslugify = (s) => s.replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

// Same city meta from CityLandingPage — kept here to keep this page self-contained
const CITY_META = {
  bengaluru: { state: "Karnataka", hero: "photo-1596176530529-78163a4f7af2", localities: ["Indiranagar", "Whitefield", "Koramangala", "HSR Layout", "Marathahalli", "Electronic City", "JP Nagar", "BTM Layout"] },
  mumbai:    { state: "Maharashtra", hero: "photo-1570168007204-dfb528c6958f", localities: ["Andheri West", "Powai", "Bandra", "Thane", "Malad", "Goregaon", "Chembur", "Navi Mumbai"] },
  delhi:     { state: "Delhi NCR", hero: "photo-1587474260584-136574528ed5", localities: ["South Delhi", "Dwarka", "Rohini", "Saket", "Vasant Kunj", "Karol Bagh", "Lajpat Nagar", "Greater Kailash"] },
  pune:      { state: "Maharashtra", hero: "photo-1705955463252-e3f670e4041b", localities: ["Kothrud", "Hinjewadi", "Wakad", "Baner", "Kharadi", "Viman Nagar", "Aundh", "Hadapsar"] },
  hyderabad: { state: "Telangana", hero: "photo-1741545979534-02f59c742730", localities: ["Gachibowli", "Madhapur", "Kondapur", "Hitech City", "Kukatpally", "Jubilee Hills", "Banjara Hills", "Miyapur"] },
  chennai:   { state: "Tamil Nadu", hero: "photo-1582510003544-4d00b7f74220", localities: ["Anna Nagar", "T. Nagar", "Adyar", "Velachery", "OMR", "Porur", "Nungambakkam", "Perungudi"] },
  gurgaon:   { state: "Haryana", hero: "photo-1697135376181-f6b7aac6caf6", localities: ["DLF Phase 1-5", "Sohna Road", "Golf Course Road", "Sector 56", "Sector 82", "MG Road", "Udyog Vihar", "Cyber City"] },
  jodhpur:   { state: "Rajasthan", hero: "photo-1669548329838-45008f9a45fc", localities: ["Ratanada", "Sardarpura", "Chopasni Housing Board", "Paota", "Shastri Nagar", "Basni", "Air Force Area", "New Power House Road"] },
  jaipur:    { state: "Rajasthan", hero: "photo-1599661046289-e31897846e41", localities: ["Malviya Nagar", "Mansarovar", "Vaishali Nagar", "C-Scheme", "Raja Park", "Jagatpura", "Tonk Road", "Sirsi Road"] },
  udaipur:   { state: "Rajasthan", hero: "photo-1695956353120-54ce5e91632b", localities: ["Fatehpura", "Hiran Magri", "Bhuwana", "Sector 4", "Sector 11", "Rani Road", "Sisarma", "Bedla"] },
  kolkata:   { state: "West Bengal", hero: "photo-1558431382-27e303142255", localities: ["Salt Lake", "New Town", "Ballygunge", "Alipore", "Behala", "Jadavpur", "Park Street", "Rajarhat"] },
  ahmedabad: { state: "Gujarat", hero: "photo-1687840430404-b82859282de4", localities: ["Bopal", "SG Highway", "Prahlad Nagar", "Satellite", "Bodakdev", "Naranpura", "Vastrapur", "Chandkheda"] },
  goa:       { state: "Goa", hero: "photo-1512343879784-a960bf40e7f2", localities: ["Panaji", "Margao", "Vasco", "Calangute", "Anjuna", "Baga", "Candolim", "Porvorim"] },
  agra:      { state: "Uttar Pradesh", hero: "photo-1548013146-72479768bada", localities: ["Dayalbagh", "Kamla Nagar", "Sikandra", "Tajganj", "Sadar Bazar", "Shahganj", "Khandari", "Balkeshwar"] },
  varanasi:  { state: "Uttar Pradesh", hero: "photo-1706186839147-0d708602587b", localities: ["Sigra", "Sunderpur", "Lanka", "Bhelupur", "Cantt", "Mahmoorganj", "Nadesar", "Assi"] },
};

export { CITY_META, slugify, deslugify };

export default function LocalityLandingPage() {
  const { city, locality } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const cityKey = (city || "").toLowerCase();
  const meta = CITY_META[cityKey];
  const localityName = meta ? (meta.localities.find((l) => slugify(l) === locality) || deslugify(locality)) : deslugify(locality || "");

  const [items, setItems] = useState([]);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get("/properties", { params: { city: cityKey, locality: localityName, listing_type: "rent", limit: 60 } })
      .then(({ data }) => {
        setItems(data.slice(0, 12));
        setCount(data.length);
      })
      .finally(() => setLoading(false));
  }, [cityKey, localityName]);

  if (!meta) {
    return (
      <div className="max-w-4xl mx-auto p-16 text-center">
        <p className="font-display text-3xl text-[#7A1E2A] mb-3">City not supported yet</p>
        <Link to="/properties" className="tlr-gold-btn rounded-full px-6 py-3 inline-block">Browse all</Link>
      </div>
    );
  }

  const cityCap = cityKey.charAt(0).toUpperCase() + cityKey.slice(1);
  const title = `${localityName}, ${cityCap} — Homes for Rent from Owners`;
  const description = `${count > 0 ? `${count} verified` : "Fresh"} rental listings in ${localityName}, ${cityCap}. Rent 1 BHK, 2 BHK and 3 BHK flats directly from owners. Zero brokerage, verified listings.`;
  const heroImg = `https://images.unsplash.com/${meta.hero}?w=1600&auto=format&q=80`;
  const faqs = LOCALITY_FAQS(localityName, cityCap);

  return (
    <div>
      <SEO
        title={title}
        description={description}
        path={`/rent/${cityKey}/${locality}`}
        image={heroImg}
        keywords={`${localityName} rent, ${localityName} ${cityCap} flat, 2BHK ${localityName}, ${localityName} apartment, no brokerage ${localityName}, owner rental ${localityName} ${cityCap}`}
        breadcrumbs={[
          { name: "Rent", path: "/properties?listing_type=rent" },
          { name: cityCap, path: `/rent/${cityKey}` },
          { name: localityName, path: `/rent/${cityKey}/${locality}` },
        ]}
        faqs={faqs}
      />

      {/* Hero */}
      <section className="relative min-h-[44vh] flex items-end overflow-hidden">
        <img src={heroImg} alt={`${localityName}, ${cityCap}`} className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 hero-scrim" />
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10 pb-14 pt-32 w-full text-white">
          <Breadcrumbs items={[
            { name: "Rent", path: "/properties?listing_type=rent" },
            { name: cityCap, path: `/rent/${cityKey}` },
            { name: localityName, path: `/rent/${cityKey}/${locality}` },
          ]} />
          <p className="label-eyebrow text-[#C9A24A] mb-3">{cityCap} · {meta.state}</p>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-tight max-w-3xl mb-4">
            Rentals in <em className="not-italic text-[#C9A24A]">{localityName}</em>
          </h1>
          <p className="text-white/85 max-w-2xl text-lg">
            {count > 0
              ? <><b className="text-white">{count}</b> live listings in {localityName}. Direct from owners. </>
              : <>Fresh listings in {localityName} land every week. </>}
            Zero brokerage · 10 free owner reveals on signup.
          </p>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-12 space-y-14">
        {/* Live listings */}
        <section>
          <div className="flex items-end justify-between mb-6">
            <div>
              <p className="label-eyebrow text-gray-500 mb-2">Live listings</p>
              <h2 className="font-display text-3xl text-[#7A1E2A]">Homes in {localityName}</h2>
            </div>
            <Link to={`/properties?listing_type=rent&city=${encodeURIComponent(cityCap)}&locality=${encodeURIComponent(localityName)}`} className="text-sm text-[#7A1E2A] hover:underline">View all →</Link>
          </div>
          {loading ? (
            <div className="text-center py-16 text-gray-500">Loading listings…</div>
          ) : items.length === 0 ? (
            <div className="bg-white border border-[#EFE3D0] rounded-2xl p-10 text-center">
              <p className="font-display text-2xl text-[#7A1E2A] mb-2">First listing in {localityName} coming soon</p>
              <p className="text-gray-500 mb-4">Be the first owner to list a home in {localityName} — 60 seconds, free forever.</p>
              <Button onClick={() => nav("/list-property")} className="tlr-gold-btn rounded-full h-11 px-6">List your property</Button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {items.map((p) => (
                <PropertyCard key={p.id} property={p} isFav={user && user.favorites?.includes(p.id)} />
              ))}
            </div>
          )}
        </section>

        {/* SEO copy */}
        <section className="bg-white border border-[#EFE3D0] rounded-2xl p-8 md:p-10">
          <h2 className="font-display text-3xl text-[#7A1E2A] mb-4">Renting in {localityName}, {cityCap}</h2>
          <p className="text-gray-700 leading-relaxed mb-4">
            {localityName} is one of the most sought-after neighbourhoods in {cityCap} for renters who want a mix of convenience,
            connectivity and lifestyle. Whether you're looking for a 1 BHK for solo living, a 2 BHK for a young couple, or a
            larger 3 BHK for family relocation, listings from owners across {localityName} give you the widest choice — without
            any broker cut.
          </p>
          <p className="text-gray-700 leading-relaxed mb-4">
            Every listing on To-Let Rentals is manually reviewed before it goes live. Owners can add a video walkthrough so you
            get a real feel of the home before scheduling a visit. Use our AI Rent Estimator to check a fair monthly rent for
            {" "}{localityName} before you negotiate.
          </p>
          <p className="text-gray-700 leading-relaxed">
            First-time renters get 10 free owner-contact reveals on signup — no credit card required. Rentals in {localityName}
            typically require a security deposit of 3-10 months' rent depending on furnishing and property type. Read our
            {" "}<Link to="/blog/rent-agreement-checklist-india" className="text-[#7A1E2A] underline">Rent Agreement Checklist</Link>
            {" "}before you sign anything.
          </p>
        </section>

        {/* Other localities in same city */}
        <section>
          <p className="label-eyebrow text-gray-500 mb-3">More {cityCap} neighbourhoods</p>
          <h2 className="font-display text-3xl text-[#7A1E2A] mb-6">Rent in nearby areas</h2>
          <div className="flex flex-wrap gap-2">
            {meta.localities.filter((l) => l !== localityName).map((loc) => (
              <Link
                key={loc}
                to={`/rent/${cityKey}/${slugify(loc)}`}
                className="px-4 py-2 rounded-full bg-white border border-[#EFE3D0] text-sm text-gray-700 hover:border-[#7A1E2A] hover:text-[#7A1E2A] transition-colors inline-flex items-center gap-1.5"
                data-testid={`nearby-${slugify(loc)}`}
              >
                <MapPin size={12} /> {loc}
              </Link>
            ))}
          </div>
        </section>

        {/* FAQs — visible + FAQPage JSON-LD via SEO */}
        <FAQSection faqs={faqs} title={`FAQs about renting in ${localityName}`} />
      </div>
    </div>
  );
}
